'use client';

import { Ruler, PenTool, Clock, Gem } from 'lucide-react';

const STEPS = [
  {
    num: '01',
    title: 'Le Croquis',
    duration: 'Jour 1',
    text: "Rendez-vous privé à l'atelier. Nous écoutons votre souhait, dessinons à main levée, choisissons ensemble le cuir, la teinte et les finitions. Chaque commande sur mesure commence par une conversation.",
  },
  {
    num: '02',
    title: 'Le Patron',
    duration: 'Jour 2 — 3',
    text: "Le maître patronnier trace le gabarit sur papier kraft, l'ajuste au millimètre, valide les proportions. C'est l'architecture invisible qui fera la différence entre un bel objet et une pièce qui épouse votre geste.",
  },
  {
    num: '03',
    title: 'La Coupe & Couture',
    duration: 'Jours 4 — 8',
    text: "Le cuir sélectionné est posé sur la table, inspecté une dernière fois, puis coupé au scalpel. La couture sellier commence, au fil de lin ciré, point par point, à raison de sept points par centimètre.",
  },
  {
    num: '04',
    title: 'La Patine',
    duration: 'Jours 9 — 11',
    text: "Sept couches de cire d'abeille teintée, appliquées au chiffon, polies à la brosse de chèvre entre chaque couche. C'est ici que naît la profondeur, le reflet, l'âme de la pièce.",
  },
  {
    num: '05',
    title: 'La Livraison',
    duration: 'Jour 12',
    text: "Présentation privée à l'atelier, en présence du maître. Votre pièce est numérotée, livrée dans son écrin gravé, accompagnée de son certificat d'authenticité et de sa garantie à vie.",
  },
];

const BESPOKE_FEATURES = [
  { icon: Ruler, label: 'Coupes personnalisées', text: 'Aucune mesure standardisée. Tout est tracé à votre morphologie.' },
  { icon: PenTool, label: 'Monogramme gravé', text: 'Vos initiales cousues main ou gravées au fer chaud.' },
  { icon: Clock, label: 'Délai douze jours', text: 'De la première rencontre à la livraison, douze jours ouvrés.' },
  { icon: Gem, label: 'Pièce unique numérotée', text: 'Chaque commande porte un numéro et un nom gravé à la main.' },
];

export default function SurMesure() {
  return (
    <section
      id="sur-mesure"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="/images/sur-mesure.png"
          alt="Mains d'artisan traçant un patron sur cuir"
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(0.32) saturate(1.15)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,15,0.85) 0%, rgba(10,10,15,0.55) 50%, rgba(10,10,15,0.92) 100%)',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Sur Mesure</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-gold-gradient">Le sur mesure,</span>
            <br />
            <span className="text-[#F4E8C1]">art suprême du cuir.</span>
          </h2>
          <p className="max-w-3xl mx-auto text-[#F4E8C1]/70 font-light leading-relaxed">
            Au-delà de nos collections, la maison MATAR propose un service de
            création sur mesure : un sac imaginé pour vous, une veste taillée à
            votre silhouette, un objet qui n'existe que pour vous. Douze jours,
            cinq étapes, une pièce unique au monde.
          </p>
        </div>

        {/* Bespoke features */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {BESPOKE_FEATURES.map((f) => (
            <div
              key={f.label}
              className="bg-[#12121a]/70 backdrop-blur-sm border border-[#D4AF37]/20 p-6 hover:border-[#D4AF37]/50 transition-colors duration-500 text-center"
            >
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center border border-[#D4AF37]/30 rounded-full">
                <f.icon size={20} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-display text-sm font-semibold text-[#F4E8C1] mb-2 tracking-wide uppercase">
                {f.label}
              </h3>
              <p className="text-xs text-[#F4E8C1]/60 leading-relaxed font-light">
                {f.text}
              </p>
            </div>
          ))}
        </div>

        {/* Process timeline */}
        <div className="bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-7 sm:p-12">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="block h-px w-10 bg-[#D4AF37]" />
              <span className="eyebrow">Douze Jours, Cinq Sacrements</span>
              <span className="block h-px w-10 bg-[#D4AF37]" />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#F4E8C1]">
              Le rituel de la création
            </h3>
          </div>

          <div className="grid md:grid-cols-5 gap-5">
            {STEPS.map((step, idx) => (
              <div
                key={step.num}
                className="relative group"
              >
                {/* Connector line */}
                {idx < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-7 left-[60%] right-[-20%] h-px bg-gradient-to-r from-[#D4AF37]/40 to-transparent" />
                )}

                <div className="relative bg-[#0a0a0f]/60 border border-[#D4AF37]/20 p-5 h-full hover:border-[#D4AF37]/50 transition-colors duration-400">
                  {/* Number */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full border border-[#D4AF37]/40 flex items-center justify-center font-display text-base font-bold text-gold-gradient">
                      {step.num}
                    </div>
                  </div>

                  <div className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">
                    {step.duration}
                  </div>
                  <h4 className="font-display text-base font-semibold text-[#F4E8C1] mb-2 tracking-wide">
                    {step.title}
                  </h4>
                  <p className="text-xs text-[#F4E8C1]/60 leading-relaxed font-light">
                    {step.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-10 text-center">
            <p className="font-serif italic text-base sm:text-lg text-[#F4E8C1]/80 mb-6">
              « Une pièce sur mesure n'est jamais une dépense. C'est un héritage
              que l'on transmet. »
            </p>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-3 px-9 py-4 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.18em] text-xs hover:glow-gold-strong transition-all duration-500"
            >
              Demander une création sur mesure
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
